import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Input,
    Button as AntButton,
    message,
    Modal,
    Tag
} from 'antd';
import styled from 'styled-components';
import {
    getCompanyDefaultJobExtensionFields,
    createCompanyDefaultJobExtensionField,
    updateCompanyDefaultJobExtensionField,
    deleteCompanyDefaultJobExtensionField
} from '../../api/company';
import { CompanyDefaultJobExtensionField } from '../../types';
import {
    ExclamationCircleOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useAppSelector } from '../../store/hooks';
import useHirePortTheme from '../../hooks/useHirePortTheme';

const { confirm } = Modal;

// --- Reused Styled Components (Consider moving to UI/ if used elsewhere) ---
const Container = styled.div`
  max-width: 100%;
  padding: 0;
`;

const FieldCard = styled.div`
  border-radius: 8px;
  margin-bottom: 1rem;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s;
  position: relative;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
  
  &.editing {
    background-color: #f9fafb;
    border: 1px solid #e8eef4;
  }
`;

const FieldNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const FieldName = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.71;
  color: #061c2e;
`;

const EditableInput = styled(Input)`
  flex: 1;
  margin-right: 16px;
  
  &.ant-input {
    border-radius: 4px;
    border: 1px solid #d9d9d9;
    padding: 4px 11px;
    
    &:hover, &:focus {
      border-color: ${({ theme }) => theme.primaryColor};
      box-shadow: none;
    }
  }
`;

const FieldActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled(AntButton)`
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const SmallAddButton = styled(AntButton)`
  font-size: 0.75rem;
  height: 32px;
  padding: 0 8px;
  
  &.ant-btn {
    border-radius: ${({ theme }) => theme.radius};
    border-color: ${({ theme }) => theme.primaryColor};
    color: ${({ theme }) => theme.primaryColor};
    background-color: transparent;
    box-shadow: none;
    
    &:hover {
      color: ${({ theme }) => theme.primaryColorLight || theme.hoverColor};
      border-color: ${({ theme }) => theme.primaryColorLight || theme.hoverColor};
    }
  }
`;

const StyledInheritedTag = styled(Tag)`
  &.ant-tag {
    border-radius: ${({ theme }) => theme.radius};
  }
`;

// Add back EmptyStateWrapper and EmptyStateIcon
const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: #f9fafb;
  border: 1px dashed #aebeca;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: #aebeca;
  margin-bottom: 1rem;
`;

const CompanyDefaultJobExtensionFieldsForm: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAppSelector((state) => state.user);
    const currentUserCompanyId = user?.company?.id;
    const theme = useHirePortTheme();

    const [fields, setFields] = useState<CompanyDefaultJobExtensionField[]>([]);
    const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState('');
    const [addingNewField, setAddingNewField] = useState(false);
    const [newFieldValue, setNewFieldValue] = useState('');

    const fetchFields = useCallback(async () => {
        try {
            const response = await getCompanyDefaultJobExtensionFields();
            setFields(response.data);
        } catch (error) {
            console.error('Error fetching company default extension fields', error);
            message.error(t('ERROR_FETCHING_COMPANY_DEFAULTS', 'Failed to load company default questions.'));
        }
    }, [t]);

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    const startEditing = (field: CompanyDefaultJobExtensionField) => {
        if (field.company !== currentUserCompanyId) return;
        setEditingFieldId(field.id);
        setEditingValue(field.name);
    };

    const cancelEditing = () => {
        setEditingFieldId(null);
        setEditingValue('');
    };

    const saveEdit = async () => {
        if (!editingFieldId || !editingValue.trim()) {
            return;
        }
        try {
            const fieldToUpdate = fields.find(f => f.id === editingFieldId);
            if (fieldToUpdate && fieldToUpdate.company !== currentUserCompanyId) {
                message.error(t('ERROR_CANNOT_EDIT_INHERITED', 'Cannot edit inherited fields.'));
                return;
            }
            await updateCompanyDefaultJobExtensionField(editingFieldId, { name: editingValue });
            message.success(t('DEFAULT_FIELD_UPDATED_SUCCESS', 'Default field updated successfully'));
            setEditingFieldId(null);
            setEditingValue('');
            fetchFields();
        } catch (error) {
            console.error('Error updating default field', error);
            message.error(t('ERROR_UPDATING_DEFAULT_FIELD', 'Error updating default field'));
        }
    };

    const startAddingField = () => {
        setAddingNewField(true);
        setNewFieldValue('');
    };

    const cancelAddingField = () => {
        setAddingNewField(false);
        setNewFieldValue('');
    };

    const saveNewField = async () => {
        if (!newFieldValue.trim()) {
            return;
        }
        try {
            await createCompanyDefaultJobExtensionField({ name: newFieldValue });
            message.success(t('DEFAULT_FIELD_ADDED_SUCCESS', 'Default field added successfully'));
            setAddingNewField(false);
            setNewFieldValue('');
            fetchFields();
        } catch (error) {
            console.error('Error adding new default field', error);
            message.error(t('ERROR_ADDING_DEFAULT_FIELD', 'Error adding default field'));
        }
    };

    const handleDelete = (field: CompanyDefaultJobExtensionField) => {
        if (field.company !== currentUserCompanyId) {
            message.error(t('ERROR_CANNOT_DELETE_INHERITED', 'Cannot delete inherited fields.'));
            return;
        }
        confirm({
            title: t('CONFIRM_DELETE_DEFAULT_FIELD_TITLE', 'Are you sure you want to delete this default field?'),
            icon: <ExclamationCircleOutlined />,
            content: t('CONFIRM_DELETE_DEFAULT_FIELD_CONTENT', 'This action cannot be undone. Jobs already using this field will not be affected.'),
            okText: t('YES', 'Yes'),
            okType: 'danger',
            cancelText: t('NO', 'No'),
            onOk: async () => {
                try {
                    await deleteCompanyDefaultJobExtensionField(field.id);
                    message.success(t('DEFAULT_FIELD_DELETED_SUCCESS', 'Default field deleted successfully'));
                    fetchFields();
                } catch (error) {
                    console.error('Error deleting default field', error);
                    message.error(t('ERROR_DELETING_DEFAULT_FIELD', 'Error deleting default field'));
                }
            },
        });
    };

    return (
        <Container>
            {fields.map(field => {
                const isInherited = field.company !== currentUserCompanyId;
                return (
                    <FieldCard key={field.id} className={editingFieldId === field.id ? 'editing' : ''}>
                        {editingFieldId === field.id && !isInherited ? (
                            <EditableInput
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                autoFocus
                                onPressEnter={saveEdit}
                            />
                        ) : (
                            <FieldNameContainer>
                                <FieldName>{field.name}</FieldName>
                                {isInherited && <StyledInheritedTag color={theme.primaryColor}>{t('INHERITED_BADGE', 'Inherited')}</StyledInheritedTag>}
                            </FieldNameContainer>
                        )}
                        <FieldActions>
                            {editingFieldId === field.id && !isInherited ? (
                                <>
                                    <ActionButton type="text" icon={<CheckOutlined />} onClick={saveEdit} title={t('SAVE', 'Save')} />
                                    <ActionButton type="text" icon={<CloseOutlined />} onClick={cancelEditing} title={t('CANCEL', 'Cancel')} />
                                </>
                            ) : (
                                <>
                                    <ActionButton
                                        type="text"
                                        icon={<EditOutlined />}
                                        onClick={() => startEditing(field)}
                                        title={t('EDIT', 'Edit')}
                                        disabled={isInherited}
                                    />
                                    <ActionButton
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDelete(field)}
                                        title={t('DELETE', 'Delete')}
                                        disabled={isInherited}
                                    />
                                </>
                            )}
                        </FieldActions>
                    </FieldCard>
                );
            })}

            {fields.length === 0 && !addingNewField && (
                <EmptyStateWrapper>
                    <EmptyStateIcon><PlusOutlined /></EmptyStateIcon>
                    <p>{t('NO_COMPANY_DEFAULTS_YET', 'No default questions defined for your company yet.')}</p>
                    <SmallAddButton icon={<PlusOutlined />} onClick={startAddingField} style={{ marginTop: '1rem' }}>
                        {t('ADD_NEW_DEFAULT_FIELD', 'Add new company default field')}
                    </SmallAddButton>
                </EmptyStateWrapper>
            )}

            {addingNewField ? (
                <FieldCard className="editing">
                    <EditableInput
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        placeholder={t('ENTER_DEFAULT_FIELD_NAME_PLACEHOLDER', 'Enter default field name')}
                        autoFocus
                        onPressEnter={saveNewField}
                    />
                    <FieldActions>
                        <ActionButton type="text" icon={<CheckOutlined />} onClick={saveNewField} title={t('SAVE', 'Save')} />
                        <ActionButton type="text" icon={<CloseOutlined />} onClick={cancelAddingField} title={t('CANCEL', 'Cancel')} />
                    </FieldActions>
                </FieldCard>
            ) : (
                <div style={{ marginTop: '1rem' }}>
                    <SmallAddButton icon={<PlusOutlined />} onClick={startAddingField}>
                        {t('ADD_NEW_DEFAULT_FIELD', 'Add new company default field')}
                    </SmallAddButton>
                </div>
            )}
        </Container>
    );
};

export default CompanyDefaultJobExtensionFieldsForm; 